import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { DetailSkeleton, ErrorState } from '@/components/ui/states'
import { Table, TableBody, TableCell, TableEmptyRow, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tooltip } from '@/components/ui/tooltip'
import { formatReturn } from '@/components/pipeline/SourceNumbers'
import { PeriodBar } from '@/components/stats/StatsPieces'
import { requestErrorMessage } from '@/helpers/apiClient'
import { instantLabel } from '@/lib/instantLabel'
import { formatAdDay, formatAdPeriod, formatCount } from '@/lib/adFormat'
import { useCompanyAds } from '@/hooks/useAds'
import type { CalendarDay } from '@lib/validators/temporal.validator'
import type { AdBookingsDto, AdPeriodWindowDto, CompanyAdsDto } from '@lib/dto/ad-numbers.dto'
import { dollarsFromCents } from '@lib/helpers/format'
import { AdNumberCells, AdNumberHeads } from './AdNumberCells'
import { RefreshAdsButton } from './RefreshAdsButton'

/** "Oct 11–24, 2026 · through Oct 13", or the period alone once it is whole. */
export function periodHeading(window: AdPeriodWindowDto): string {
  const days = formatAdPeriod(window.start, window.end)
  return window.inProgress ? `${days} · through ${formatAdDay(window.through)}` : days
}

/** What the comparison is against, said once rather than on every number. */
export function comparisonNote(window: AdPeriodWindowDto): string {
  if (!window.inProgress) return `Compared with ${formatAdPeriod(window.compareStart, window.compareEnd)}.`
  const days = window.days === 1 ? 'its first day' : `its first ${String(window.days)} days`
  return `In progress: compared with ${days} — ${formatAdPeriod(window.compareStart, window.compareEnd)} — of the period before.`
}

/** "Updated Oct 14, 6:05 AM", for the line under a table. */
export function freshnessNote(data: Pick<CompanyAdsDto, 'lastUpdatedAt'>): string | null {
  return data.lastUpdatedAt === null ? null : `Updated ${instantLabel(data.lastUpdatedAt)}.`
}

interface CompanyAdsTableProps {
  /** Show the refresh control: Google can be read here, and something is in use. */
  canRefresh: boolean
}

/** A row's Google Ads bookings, what they booked and its return; dashes where nothing records them. */
function BookingCells({ bookings }: { bookings: AdBookingsDto | null }) {
  const cell = 'whitespace-nowrap px-2.5 text-right tabular-nums'
  const dash = <span className="text-ink-muted">—</span>
  return (
    <>
      <TableCell className={cell}>{bookings === null ? dash : formatCount(bookings.bookings)}</TableCell>
      <TableCell className={cell}>{bookings?.bookedValueCents == null ? dash : dollarsFromCents(bookings.bookedValueCents)}</TableCell>
      <TableCell className={cell}>{bookings?.returnRatio == null ? dash : formatReturn(bookings.returnRatio)}</TableCell>
    </>
  )
}

/**
 * The company's table (docs/ad-analytics.spec.md, "The company's Ads page"): one
 * row per venue any numbers count toward, for one period, each compared with
 * the period before, and the company's totals per currency beneath.
 */
export function CompanyAdsTable({ canRefresh }: CompanyAdsTableProps) {
  const [period, setPeriod] = useState<CalendarDay | null>(null)
  const query = useCompanyAds(period, true)

  if (query.isPending) return <DetailSkeleton />
  if (query.isError) return <ErrorState message={requestErrorMessage(query.error)} onRetry={() => void query.refetch()} />
  const data = query.data
  const window = data.window
  if (window === null) {
    return (
      <p className="m-0 text-sm text-ink-muted">
        {data.importing
          ? 'Bringing in your accounts’ history from Google Ads. The numbers appear here when it is in.'
          : 'No numbers yet. Put an account in use below to bring its history in.'}
      </p>
    )
  }

  // Newest first, like the Leads and Pipeline pages' periods; only the newest can be under way.
  const periods = data.periodStarts.map((start, i) => ({ start, inProgress: i === 0 && window.inProgress }))

  return (
    <div className="space-y-3">
      <PeriodBar window={window} periods={periods} spend={null} onPeriod={setPeriod} />
      <Table heading={periodHeading(window)} actions={canRefresh ? <RefreshAdsButton busy={data.importing} /> : null}>
        <TableHeader>
          <TableHead>Venue</TableHead>
          <AdNumberHeads />
          <TableHead className="whitespace-nowrap px-2.5 text-right">
            <Tooltip content="Couples from Google Ads who inquired in the period and have booked.">
              <span className="cursor-default">Bookings</span>
            </Tooltip>
          </TableHead>
          <TableHead className="whitespace-nowrap px-2.5 text-right">Booked value</TableHead>
          <TableHead className="whitespace-nowrap px-2.5 text-right">Return</TableHead>
        </TableHeader>
        <TableBody>
          {data.rows.length === 0 ? <TableEmptyRow colSpan={11}>No venue has numbers in this period.</TableEmptyRow> : null}
          {data.rows.map((row) => (
            <TableRow key={row.venueId}>
              <TableCell className="min-w-32">
                <Link to={`/venues/${row.venueId}/ads`} className="font-medium text-ink underline-offset-2 hover:underline">
                  {row.venueName}
                </Link>
                {row.notUpdating ? (
                  <div>
                    <Badge tone="warning">Not all updating</Badge>
                  </div>
                ) : null}
              </TableCell>
              <AdNumberCells numbers={row.numbers} />
              <BookingCells bookings={row.bookings} />
            </TableRow>
          ))}
          {data.rows.length > 1 ? (
            <TableRow className="font-medium">
              <TableCell>All venues</TableCell>
              <AdNumberCells numbers={data.totals} />
              <BookingCells bookings={data.bookings} />
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
      <p className="m-0 text-xs text-ink-muted">
        {comparisonNote(window)} {freshnessNote(data)}
      </p>
    </div>
  )
}
